import { AutocompleteValue, ChipTypeMap, PopperProps } from '@mui/material';
import { HTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';
import { Autocomplete, AutocompleteProps } from '../Autocomplete/Autocomplete';
import styles from './InlineAutocomplete.module.scss';
import { AsyncAutocomplete } from '../AsyncAutocomplete/AsyncAutocomplete';
import { getDataIdGenerator } from '../../shared/utils/dataId';

export function InlineAutocomplete<
  A,
  B extends boolean | undefined,
  C extends boolean | undefined,
  D extends boolean | undefined,
  E extends React.ElementType = ChipTypeMap['defaultComponent']
>(
  props: AutocompleteProps<A, B, C, D, E> & {
    onChange: (value: unknown) => void;
    dataSource?: (params: unknown) => Promise<unknown>;
  }
) {
  const AutocompleteWithTsFixes = Autocomplete as (
    props: AutocompleteProps<A, B, C, D, E>
  ) => ReactNode;
  const { className, dataSource = null, dataId, onChange, ...rest } = props;
  const dataIdGenerator = getDataIdGenerator('inline-autocomplete', dataId);

  const onChangeEvent = (_e: React.SyntheticEvent, v: AutocompleteValue<A, B, C, D>) => {
    if (onChange) {
      onChange(v);
    }
  };
  if (dataSource) {
    return (
      <AsyncAutocomplete
        {...rest}
        dataSource={dataSource}
        forcePopupIcon={false}
        dataId={dataIdGenerator('')}
        open
        onChange={onChangeEvent}
        className={clsx('px-3 border-radius-4', className)}
        PopperComponent={PopperComponent}
        PaperComponent={PaperComponent}
        textFieldProps={{ ...rest.textFieldProps, className: 'mt-2', size: 'small' }}
      />
    );
  }

  return (
    <AutocompleteWithTsFixes
      {...rest}
      dataId={dataIdGenerator('')}
      forcePopupIcon={false}
      open
      onChange={onChangeEvent}
      className={clsx('px-3 border-radius-4', className)}
      PopperComponent={PopperComponent}
      PaperComponent={PaperComponent}
      textFieldProps={{ ...(rest.textFieldProps ?? {}), className: 'mt-2', size: 'small' }}
    />
  );
}

function PaperComponent(props: HTMLAttributes<HTMLElement>) {
  const { children, className, ...rest } = props;
  return (
    <div {...rest} className={clsx(styles.inlineDropdownPaper, className)}>
      {children}
    </div>
  );
}
function PopperComponent(props: PopperProps): ReactNode {
  const { disablePortal, anchorEl, open, children, ...other } = props;
  return (
    <div
      {...other}
      style={{
        width: '100%'
      }}
    >
      {typeof children === 'function' ? children({ ...props, placement: 'auto' }) : children}
    </div>
  );
}
